import type { EpisodeView } from "@supermanus/document-model";

const VIEW_LABEL: Record<EpisodeView, string> = {
  home: "Status",
  timing: "Sceneliste",
  outline: "Rundown",
  script: "Manus",
  crew: "Creditliste",
  programtext: "Programtekst",
  sources: "Noter",
  status: "Status",
};

export { VIEW_LABEL };

export const DEFAULT_BROWSER_TITLE = "Minproduktion.dk";

/** Browser tab: episode · current view. */
export function formatBrowserDocumentTitle(
  episodeTitle: string,
  view: EpisodeView,
): string {
  const ep = episodeTitle.trim() || "Unavngiven episode";
  return `${ep} · ${VIEW_LABEL[view]}`;
}

export function formatDocumentTimestamp(date: Date = new Date()): string {
  return new Intl.DateTimeFormat("da-DK", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

/** Season · episode · view — same line as on the printed page header. */
export function formatDocumentEpisodeLine(
  seasonTitle: string | null | undefined,
  episodeTitle: string,
  view: EpisodeView,
): string {
  return [seasonTitle?.trim() || null, episodeTitle.trim() || null, VIEW_LABEL[view]]
    .filter(Boolean)
    .join(" · ");
}

export interface DocumentHeadProps {
  productionTitle: string;
  seasonTitle?: string | null;
  episodeTitle: string;
  view: EpisodeView;
  /** When set, shown instead of “now”. */
  printedAt?: Date;
  /** Optional status line under the header (working views only). */
  status?: string;
}

/**
 * Document masthead — same content and layout as the printed running header.
 */
export function DocumentHead({
  productionTitle,
  seasonTitle,
  episodeTitle,
  view,
  printedAt,
  status,
}: DocumentHeadProps) {
  const whenDate = printedAt ?? new Date();
  const when = formatDocumentTimestamp(whenDate);
  const episodeLine = formatDocumentEpisodeLine(
    seasonTitle,
    episodeTitle,
    view,
  );
  return (
    <header className="sm-doc-head">
      <div className="sm-doc-head-row">
        <div className="sm-doc-head-main">
          <strong>{productionTitle}</strong>
          <span>{episodeLine}</span>
        </div>
        <time dateTime={whenDate.toISOString()}>{when}</time>
      </div>
      {status ? <p className="sm-doc-head-status">{status}</p> : null}
    </header>
  );
}
